// Granular permission structure matching backend
export interface GranularPermissions {
  dashboard: {
    main: {
      view: boolean;
      downloadReport: boolean;
    };
    analytics: {
      view: boolean;
      export: boolean;
    };
  };
  users: {
    main: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
      export: boolean;
      block: boolean;
      impersonate: boolean;
    };
  };
  programs: {
    published: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
      export: boolean;
    };
    drafts: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
      share: boolean;
    };
    categories: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
    };
  };
  payments: {
    main: {
      view: boolean;
      refund: boolean;
      export: boolean;
    };
    subscriptions: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
    };
  };
  certifications: {
    main: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
      issue: boolean;
      revoke: boolean;
    };
  };
  tickets: {
    main: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
      assignTo: boolean;
      close: boolean;
    };
  };
  supportTickets: {
    main: {
      view: boolean;
      export: boolean;
    };
    chat: {
      view: boolean;
      edit: boolean;
      assignTo: boolean;
    };
  };
  settings: {
    main: {
      view: boolean;
      edit: boolean;
    };
  };
  admin: {
    staff: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
    };
    roles: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
    };
    settings: {
      view: boolean;
      edit: boolean;
    };
  };
  forms: {
    dynamicForms: {
      view: boolean;
      edit: boolean;
    };
    appointments: {
      attendance: {
        view: boolean;
        mark: boolean;
        void: boolean;
      };
      instructors: {
        view: boolean;
        assign: boolean;
      };
    };
  };
  courseKeys: {
    main: {
      view: boolean;
      manage: boolean;
    };
  };
  journeys: {
    flows: {
      view: boolean;
      edit: boolean;
    };
    review: {
      view: boolean;
      approve: boolean;
    };
  };
  leadAutomations: {
    flows: {
      view: boolean;
      edit: boolean;
    };
    activity: {
      view: boolean;
      manage: boolean;
    };
  };
  leads: {
    inbox: {
      view: boolean;
      manage: boolean;
    };
  };
}

// Helper type for permission paths
export type PermissionPath = 
  | `dashboard.main.${keyof GranularPermissions['dashboard']['main']}`
  | `dashboard.analytics.${keyof GranularPermissions['dashboard']['analytics']}`
  | `users.main.${keyof GranularPermissions['users']['main']}`
  | `programs.published.${keyof GranularPermissions['programs']['published']}`
  | `programs.drafts.${keyof GranularPermissions['programs']['drafts']}`
  | `programs.categories.${keyof GranularPermissions['programs']['categories']}`
  | `payments.main.${keyof GranularPermissions['payments']['main']}`
  | `payments.subscriptions.${keyof GranularPermissions['payments']['subscriptions']}`
  | `certifications.main.${keyof GranularPermissions['certifications']['main']}`
  | `tickets.main.${keyof GranularPermissions['tickets']['main']}`
  | `settings.main.${keyof GranularPermissions['settings']['main']}`
  | `admin.staff.${keyof GranularPermissions['admin']['staff']}`
  | `admin.roles.${keyof GranularPermissions['admin']['roles']}`
  | `admin.settings.${keyof GranularPermissions['admin']['settings']}`
  | `forms.dynamicForms.${keyof GranularPermissions['forms']['dynamicForms']}`
  | `forms.appointments.attendance.${keyof GranularPermissions['forms']['appointments']['attendance']}`
  | `forms.appointments.instructors.${keyof GranularPermissions['forms']['appointments']['instructors']}`
  | `courseKeys.main.${keyof GranularPermissions['courseKeys']['main']}`
  | `journeys.flows.${keyof GranularPermissions['journeys']['flows']}`
  | `journeys.review.${keyof GranularPermissions['journeys']['review']}`
  | `leadAutomations.flows.${keyof GranularPermissions['leadAutomations']['flows']}`
  | `leadAutomations.activity.${keyof GranularPermissions['leadAutomations']['activity']}`
  | `leads.inbox.${keyof GranularPermissions['leads']['inbox']}`;

// Default full permissions for ADMIN
export const FULL_PERMISSIONS: GranularPermissions = {
  dashboard: {
    main: { view: true, downloadReport: true },
    analytics: { view: true, export: true },
  },
  users: {
    main: { view: true, create: true, edit: true, delete: true, export: true, block: true, impersonate: true },
  },
  programs: {
    published: { view: true, create: true, edit: true, delete: true, export: true },
    drafts: { view: true, create: true, edit: true, delete: true, share: true },
    categories: { view: true, create: true, edit: true, delete: true },
  },
  payments: {
    main: { view: true, refund: true, export: true },
    subscriptions: { view: true, create: true, edit: true, delete: true },
  },
  certifications: {
    main: { view: true, create: true, edit: true, delete: true, issue: true, revoke: true },
  },
  tickets: {
    main: { view: true, create: true, edit: true, delete: true, assignTo: true, close: true },
  },
  supportTickets: {
    main: { view: true, export: true },
    chat: { view: true, edit: true, assignTo: true },
  },
  settings: {
    main: { view: true, edit: true },
  },
  admin: {
    staff: { view: true, create: true, edit: true, delete: true },
    roles: { view: true, create: true, edit: true, delete: true },
    settings: { view: true, edit: true },
  },
  forms: {
    dynamicForms: { view: true, edit: true },
    appointments: {
      attendance: { view: true, mark: true, void: true },
      instructors: { view: true, assign: true },
    },
  },
  courseKeys: {
    main: { view: true, manage: true },
  },
  journeys: {
    flows: { view: true, edit: true },
    review: { view: true, approve: true },
  },
  leadAutomations: {
    flows: { view: true, edit: true },
    activity: { view: true, manage: true },
  },
  leads: {
    inbox: { view: true, manage: true },
  },
};

// Default empty permissions
export const EMPTY_PERMISSIONS: GranularPermissions = {
  dashboard: {
    main: { view: false, downloadReport: false },
    analytics: { view: false, export: false },
  },
  users: {
    main: { view: false, create: false, edit: false, delete: false, export: false, block: false, impersonate: false },
  },
  programs: {
    published: { view: false, create: false, edit: false, delete: false, export: false },
    drafts: { view: false, create: false, edit: false, delete: false, share: false },
    categories: { view: false, create: false, edit: false, delete: false },
  },
  payments: {
    main: { view: false, refund: false, export: false },
    subscriptions: { view: false, create: false, edit: false, delete: false },
  },
  certifications: {
    main: { view: false, create: false, edit: false, delete: false, issue: false, revoke: false },
  },
  tickets: {
    main: { view: false, create: false, edit: false, delete: false, assignTo: false, close: false },
  },
  supportTickets: {
    main: { view: false, export: false },
    chat: { view: false, edit: false, assignTo: false },
  },
  settings: {
    main: { view: false, edit: false },
  },
  admin: {
    staff: { view: false, create: false, edit: false, delete: false },
    roles: { view: false, create: false, edit: false, delete: false },
    settings: { view: false, edit: false },
  },
  forms: {
    dynamicForms: { view: false, edit: false },
    appointments: {
      attendance: { view: false, mark: false, void: false },
      instructors: { view: false, assign: false },
    },
  },
  courseKeys: {
    main: { view: false, manage: false },
  },
  journeys: {
    flows: { view: false, edit: false },
    review: { view: false, approve: false },
  },
  leadAutomations: {
    flows: { view: false, edit: false },
    activity: { view: false, manage: false },
  },
  leads: {
    inbox: { view: false, manage: false },
  },
};

